import api from './api';

export const locationService = {
  // =============================================================================
  // STATES
  // =============================================================================

  // Get all states with pagination and search
  async getStates(params = {}) {
    try {
      const response = await api.get('/states', { params });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch states');
    }
  },

  // Get state by ID
  async getState(id) {
    try {
      const response = await api.get(`/states/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch state');
    }
  },

  // Create new state (Admin only)
  async createState(stateData) {
    try {
      const response = await api.post('/states', stateData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create state');
    }
  },

  // Update state (Admin only)
  async updateState(id, stateData) {
    try {
      const response = await api.put(`/states/${id}`, stateData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update state');
    }
  },

  // Delete state (Admin only)
  async deleteState(id) {
    try {
      const response = await api.delete(`/states/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete state');
    }
  },

  // Export states to Excel
  async exportStates() {
    try {
      const response = await api.get('/states/export/excel', {
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to export states');
    }
  },

  // =============================================================================
  // DISTRICTS
  // =============================================================================

  // Get all districts with filtering by state
  async getDistricts(params = {}) {
    try {
      const response = await api.get('/districts', { params });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch districts');
    }
  },

  // Get district by ID
  async getDistrict(id) {
    try {
      const response = await api.get(`/districts/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch district');
    }
  },

  // Create new district (Admin only)
  async createDistrict(districtData) {
    try {
      const response = await api.post('/districts', districtData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create district');
    }
  },

  // Update district (Admin only)
  async updateDistrict(id, districtData) {
    try {
      const response = await api.put(`/districts/${id}`, districtData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update district');
    }
  },

  // Delete district (Admin only)
  async deleteDistrict(id) {
    try {
      const response = await api.delete(`/districts/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete district');
    }
  },

  // Get districts by state
  async getDistrictsByState(stateId) {
    try {
      const response = await api.get(`/districts/by-state/${stateId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch districts by state');
    }
  },

  // Export districts to Excel
  async exportDistricts() {
    try {
      const response = await api.get('/districts/export/excel', {
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to export districts');
    }
  },

  // =============================================================================
  // BLOCKS
  // =============================================================================

  // Get all blocks with filtering by district
  async getBlocks(params = {}) {
    try {
      const response = await api.get('/blocks', { params });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch blocks');
    }
  },

  // Get block by ID
  async getBlock(id) {
    try {
      const response = await api.get(`/blocks/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch block');
    }
  },

  // Create new block (Admin only)
  async createBlock(blockData) {
    try {
      const response = await api.post('/blocks', blockData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create block');
    }
  },

  // Update block (Admin only)
  async updateBlock(id, blockData) {
    try {
      const response = await api.put(`/blocks/${id}`, blockData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update block');
    }
  },

  // Delete block (Admin only)
  async deleteBlock(id) {
    try {
      const response = await api.delete(`/blocks/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete block');
    }
  },

  // Get blocks by district
  async getBlocksByDistrict(districtId) {
    try {
      const response = await api.get(`/blocks/by-district/${districtId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch blocks by district');
    }
  },

  // Export blocks to Excel
  async exportBlocks() {
    try {
      const response = await api.get('/blocks/export/excel', {
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to export blocks');
    }
  },

  // =============================================================================
  // CENTRES
  // =============================================================================

  // Get all centres with filtering by block and type
  async getCentres(params = {}) {
    try {
      const response = await api.get('/centres', { params });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch centres');
    }
  },

  // Get centre by ID
  async getCentre(id) {
    try {
      const response = await api.get(`/centres/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch centre');
    }
  },

  // Create new centre (Admin only)
  async createCentre(centreData) {
    try {
      const response = await api.post('/centres', centreData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create centre');
    }
  },

  // Update centre (Admin only)
  async updateCentre(id, centreData) {
    try {
      const response = await api.put(`/centres/${id}`, centreData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update centre');
    }
  },

  // Delete centre (Admin only)
  async deleteCentre(id) {
    try {
      const response = await api.delete(`/centres/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete centre');
    }
  },

  // Get centres by block
  async getCentresByBlock(blockId) {
    try {
      const response = await api.get(`/centres/by-block/${blockId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch centres by block');
    }
  },

  // Get centres by type
  async getCentresByType(type) {
    try {
      const response = await api.get(`/centres/by-type/${type}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch centres by type');
    }
  },

  // Export centres to Excel
  async exportCentres() {
    try {
      const response = await api.get('/centres/export/excel', {
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to export centres');
    }
  },

  // =============================================================================
  // HIERARCHICAL OPERATIONS
  // =============================================================================

  // Get complete location hierarchy
  async getLocationHierarchy() {
    try {
      const response = await api.get('/location-hierarchy/hierarchy');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch location hierarchy');
    }
  },

  // Get location tree for specific level
  async getLocationTree(level) {
    try {
      const response = await api.get(`/location-hierarchy/tree/${level}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch location tree');
    }
  },

  // Get location breadcrumb path
  async getLocationPath(type, id) {
    try {
      const response = await api.get(`/location-hierarchy/path/${type}/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch location path');
    }
  },

  // Get location statistics
  async getLocationStats() {
    try {
      const response = await api.get('/location-hierarchy/stats');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch location statistics');
    }
  },
};
